import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useUser } from './UserContext';
import {
  IGetUserVehiclePagedResult,
  IPagedParams,
  useVehicleInfo,
} from '../hooks/useUserVehicles';
import {
  IConsultsPackageDTO,
  getStatusConsultsPackage,
  postConsultsPackage,
  getConsultsPackageBySub,
  postBatchConsultsByVehiclePlate,
} from '../hooks/useDebtsUserVehicle';
import { consultaRunningStatuses, countSuccessfulAndFailedDebtConsults, getUniqueVehiclePlates } from '../utils/batchConsultUtils';
import { IConsultStatusEnum, ICreateBatchConsultResponse, IGetBatchConsultStatusResponse } from '../hooks/types/BatchConsults';
import { IDebts } from '../hooks/useDebts';

export enum IConsultsPackageStatusTypeEnum {
  CONFIRM = 'confirm',
  LOADING = 'loading',
  FINISHED = 'finished'
}

export interface IConsultsPackageModal {
  type: 'confirm' | 'loading' | 'finished';
  success: boolean;
  message?: string | number;
}

interface VehicleContextType {
  pagedVehicles: IGetUserVehiclePagedResult | undefined;
  isLoading: boolean;
  error: any;
  refetch: () => void;
  vehiclesTotal: number;
  pagedVehicleParams: IPagedParams;
  setPagedVehicleParams: (params: IPagedParams) => void;
  handleConsultsPackageStatus: (batchCode: number) => Promise<void>;
  setConsultsPackageModal: (modal: IConsultsPackageModal | undefined) => void;
  consultsPackageModal: IConsultsPackageModal | undefined;
  handlePostConsultsPackageByUserSub: () => Promise<void>;
  batchDebtsConsultsProgress: number;
  packAndPostConsultsByDebts: (selectedDebts: IDebts[]) => Promise<void>;
  consultsCompletedStatus: number | undefined;
  successfulAndFailedDebtConsults: { successful: number, failed: number };
  consultsPackageStatus: IGetBatchConsultStatusResponse | undefined;
  currentDebtsConsultsBatch: ICreateBatchConsultResponse | undefined;
  arrayActionsConfirmModal: Array<() => void>;
  loadingConsult: boolean;
  setLoadingConsult: (load: boolean) => void;
  setArrayActionsConfirmModal: (action: Array<() => void>) => void;
  handlePostConsultsPackageByUserVehicleId: () => Promise<void>;
  isChecked: { [key: string]: boolean } | any;
  setIsChecked: (item: { [key: string]: boolean } | any) => void;
  latestConsultsPackageStatus: IGetBatchConsultStatusResponse | undefined;
  setIsAllChecked: (item: boolean) => void;
  setLatestConsultsPackageStatus: (consultsPackageStatus:IGetBatchConsultStatusResponse | undefined) => void;
  isAllChecked: boolean;
}

const VehicleContext = createContext<VehicleContextType | undefined>(undefined);

interface VehicleProviderProps {
  children: ReactNode;
}

export function VehicleProvider({ children }: VehicleProviderProps) {
  const { isLoggedIn } = useUser();

  // States related to batch debt consultations
  const [currentDebtsConsultsBatch, setCurrentDebtsConsultsBatch] = useState<ICreateBatchConsultResponse | undefined>()
  const [consultsPackageStatus, setConsultsPackageStatus] = useState<IGetBatchConsultStatusResponse | undefined>();
  const [latestConsultsPackageStatus, setLatestConsultsPackageStatus] = useState<IGetBatchConsultStatusResponse | undefined>();
  const [successfulAndFailedDebtConsults, setSuccessfulAndFailedDebtConsults] = useState<{ 
    successful: number, 
    failed: number, 
    successfulNoDebts: number 
  }>({ 
    successful: 0, 
    failed: 0, 
    successfulNoDebts: 0 
  })
  const [consultsCompletedStatus, setConsultsCompletedStatus ] = useState<number | undefined>();
  const [consultsPackageModal, setConsultsPackageModal] = useState<IConsultsPackageModal | undefined>(undefined);
  const [arrayActionsConfirmModal, setArrayActionsConfirmModal] = useState<Array<() => void>>([]);
  const [loadingConsult, setLoadingConsult] = useState<boolean>(false);

  // States related to vehicles
  const [vehiclesTotal, setVehiclesTotal] = useState<number>(0);
  const [pagedVehicleParams, setPagedVehicleParams] = useState<IPagedParams>({
    resultsPerPage: 100,
    page: 1,
    orderByDirection: 'desc',
    orderBy: 'updated_at',
    enabled: isLoggedIn,
  });

  // States for vehicle selection
  const [isChecked, setIsChecked] = useState<{ [key: string]: boolean }>({});
  const [isAllChecked, setIsAllChecked] = useState(false);

  // Reference for the status of batch debt consultations
  const consultsPackageStatusRef = useRef(consultsPackageStatus);
  consultsPackageStatusRef.current = consultsPackageStatus;
  const statusCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);


  // Calculation of the progress of batch debt consultations
  const batchDebtsConsultsProgress = useMemo(() => {
    if (consultsPackageStatus && consultsPackageStatus.consultas.length > 0) {
      const completed = consultsPackageStatus.consultas.filter(
        (c) => c.status !== IConsultStatusEnum.IN_PROGRESS,
      ).length;
      setConsultsCompletedStatus(completed)
      return (completed / consultsPackageStatus.consultas.length) * 100;
    }
    return 0;
  }, [consultsPackageStatus]);

  const { data, isLoading, error, refetch } = useVehicleInfo(pagedVehicleParams);

  useEffect(() => {
    setVehiclesTotal(data?.total ?? 0);
  }, [data]);

  // Function to check the status of batch debt consultations
  const handleConsultsPackageStatus = async (batchCode: number) => {
    try {
      const result = await getStatusConsultsPackage(batchCode);

      
      console.debug('handleConsultsPackageStatus', result);

      if (!result) {
        console.debug("Resultado vazio ou indefinido.");
        setConsultsPackageModal({
          type: 'finished',
          success: false,
          message: "Resultado da consulta está vazio.",
        });
        setConsultsPackageStatus(undefined);
        setSuccessfulAndFailedDebtConsults({ successful: 0, failed: 0, successfulNoDebts: 0 });
        setCurrentDebtsConsultsBatch(undefined);
        await refetch();
        return;
      }

      const { status, consultas } = result;

      // Count how many were successful and how many failed, regardless of the overall state
      const debtsStats = countSuccessfulAndFailedDebtConsults(consultas);
      setSuccessfulAndFailedDebtConsults(debtsStats);

      // Statuses that indicate that the consultation has not yet ended


      const isConsultRunning = consultaRunningStatuses.includes(status);
      const isAllConsultsEnded = consultas.length == (debtsStats.successful + debtsStats.failed);

      if (isConsultRunning && !isAllConsultsEnded) {
        console.debug("Atualizando Status!\nStatus: ", status);
        setConsultsPackageStatus(result);
        statusCheckTimeoutRef.current = setTimeout(() => handleConsultsPackageStatus(batchCode), 15000);
      } else {
        console.debug("Finalizado Status!\nStatus: ", status);
        
        let finalMessage = ''
        const isSuccess = status === IConsultStatusEnum.PAYABLE;

        if (isSuccess) {
          finalMessage += `Processo finalizado com sucesso. 
          ${debtsStats.successful} consultas podem ser pagas.`

          if (debtsStats.failed > 0) {
            finalMessage = `Consultas com falhas: ${debtsStats.failed}.`;
          } 
          if (debtsStats.successfulNoDebts){
            finalMessage += `Oba! Não há pendências em 
            ${debtsStats.successfulNoDebts} consultas.`
          }

        }

        setConsultsPackageModal({
          type: 'finished',
          success: isSuccess,
          message: finalMessage,
        });
        setLatestConsultsPackageStatus(result)
        setConsultsPackageStatus(undefined);
        setCurrentDebtsConsultsBatch(undefined);
        await refetch();
      }
    } catch (error) {
      console.error('Error in handleConsultsPackageStatus:', error);
      setConsultsPackageStatus(undefined);
      setSuccessfulAndFailedDebtConsults({ successful: 0, failed: 0, successfulNoDebts: 0  });
      setConsultsPackageModal({
        type: 'finished',
        success: false,
        message: "Erro ao processar a consulta.",
      });
    }
  };


  // Prepare and send Consults User vehicle IDs
  const packAndPostConsultsByUserVehicleId = async () => {
    setLoadingConsult(true);

    const consultsPackage = data?.results
      ?.filter((each) => isChecked[each.vehicle.placa])
      .map((userVehicle) => ({
        userVehicleId: userVehicle.id,
        firstConsult: false,
        origin: 'frotas',
      }));

    if (consultsPackage && consultsPackage.length > 0) {
      const results = await postConsultsPackage(consultsPackage);
      console.debug("Request finished", results);

      if (results && results.success && results.id) {
        setCurrentDebtsConsultsBatch(results)
        handleConsultsPackageStatus(results.id);

        setLoadingConsult(false);
        setConsultsPackageModal({
          type: 'loading',
          success: true,
          message: results.quantity,
        });
      } else {
        setLoadingConsult(false);
        setConsultsPackageModal({
          type: 'loading',
          success: false,
          message: results?.message,
        });
      }
    }
  };

  const packAndPostConsultsByDebts = async (selectedDebts: IDebts[] ) => {
    setLoadingConsult(true);
    const uniquePlates = getUniqueVehiclePlates(selectedDebts)
    const consultsPackage = uniquePlates.map((plate) => ({
      userVehiclePlate: plate,
      firstConsult: false,
      origin: 'frotas',
    }));

    if (consultsPackage && consultsPackage.length > 0) {
      const results = await postBatchConsultsByVehiclePlate(consultsPackage);
      console.debug("Request finished", results);

      if (results && results.success && results.id) {
        setCurrentDebtsConsultsBatch(results)
        handleConsultsPackageStatus(results.id);

        setLoadingConsult(false);
        setConsultsPackageModal({
          type: 'loading',
          success: true,
          message: results.quantity,
        });
      } else {
        setLoadingConsult(false);
        setConsultsPackageModal({
          type: 'loading',
          success: false,
          message: results?.message,
        });
      }
    }
  };

  // Função para preparar e enviar consultas por sub de usuário
  const packAndPostConsultsBySub = async () => {
    setLoadingConsult(true);

    const results = await getConsultsPackageBySub();

    setLoadingConsult(false);

    if (results && results.success && results.id) {
      handleConsultsPackageStatus(results.id);
      setConsultsPackageModal({
        type: 'loading',
        success: true,
      });
    } else {
      setConsultsPackageModal({
        type: 'loading',
        success: false,
        message: results?.message,
      });
    }
  };

  // Handlers para ações de atualização de frota
  const handlePostConsultsPackageByUserVehicleId = async () => {
    const selectedPlates = Object.entries(isChecked)
      .filter(([, value]) => value)
      .map(([key]) => key);

    if (selectedPlates.length === 0) {
      return;
    }

    setArrayActionsConfirmModal([() => packAndPostConsultsByDebts(
      selectedPlates.map(plate => ({ vehiclePlate: plate } as IDebts))
    )]);
  };

  const handlePostConsultsPackageByUserSub = async () => {
    setArrayActionsConfirmModal([() => packAndPostConsultsBySub()]);
    setConsultsPackageModal({
      type: 'confirm',
      success: true,
    });
  };

  // Valor do contexto
  const value = useMemo(() => ({
    vehiclesTotal,
    pagedVehicles: data,
    isLoading,
    error,
    refetch,
    setPagedVehicleParams,
    pagedVehicleParams,
    handleConsultsPackageStatus,
    setConsultsPackageModal,
    consultsPackageModal,
    consultsCompletedStatus,
    loadingConsult,
    setLoadingConsult,
    packAndPostConsultsByDebts,
    arrayActionsConfirmModal,
    handlePostConsultsPackageByUserSub,
    batchDebtsConsultsProgress,
    consultsPackageStatus,
    currentDebtsConsultsBatch,
    successfulAndFailedDebtConsults,
    setArrayActionsConfirmModal,
    handlePostConsultsPackageByUserVehicleId,
    setIsChecked,
    isChecked,
    setIsAllChecked,
    isAllChecked,
    latestConsultsPackageStatus,
    setLatestConsultsPackageStatus,
  }), [
    data,
    isLoading,
    error,
    refetch,
    pagedVehicleParams,
    vehiclesTotal,
    handleConsultsPackageStatus,
    consultsPackageModal,
    loadingConsult,
    successfulAndFailedDebtConsults,
    arrayActionsConfirmModal,
    consultsCompletedStatus,
    batchDebtsConsultsProgress,
    consultsPackageStatus,
    currentDebtsConsultsBatch,
    isChecked,
    isAllChecked,
  ]);

  return (
    <VehicleContext.Provider value={value}>
      {children}
    </VehicleContext.Provider>
  );
}

export function useVehicle() {
  const context = useContext(VehicleContext);
  if (!context) {
    throw new Error('useVehicle must be used within a VehicleProvider');
  }
  return context;
}
